/**
 * The shape every SQL engine profile fills in, and the context
 * {@link createSqlBackend} (`./create-sql-backend`) hands to the parts of a
 * profile that need the assembled pipeline rather than raw configuration.
 *
 * A profile is a HEAD of plain data and dialect closures that exist before
 * any backend object does, plus an opaque `assembly` ({@link EngineAssembly},
 * `./assembly`) wrapping the operation-backend builder and the late-member
 * factory for the handful of members that are self-referential (a
 * transaction's own body calls back into the backend that opened it) or that
 * otherwise need the assembled pipeline. Nothing here ever closes over a
 * `backend` binding directly — `EngineAssemblyContext.self()` is the one
 * sanctioned way a late member reaches the backend `createSqlBackend` is
 * building, and it resolves only once that backend object exists.
 */
import { ConfigurationError } from "../../../errors";
import type { ResolvedSqlTableNames } from "../../../query/compiler/schema";
import type { FulltextStrategy } from "../../../query/dialect/fulltext-strategy";
import type { SqlDialect } from "../../../query/dialect/types";
import type { VectorStrategy } from "../../../query/dialect/vector-strategy";
import type {
  FenceSql,
  WriteFencePlan,
  WriteFenceTarget,
} from "../../capabilities/write-fence";
import type { BackendResourceAudit } from "../../transaction-resource";
import type {
  AdapterBackend,
  BackendCapabilities,
  BackendCatalogProbes,
  EngineRecordedTimeMembers,
  LineageMembers,
  SchemaProvisioning,
  TransactionBackend,
} from "../../types";
import type { ContributionMaterializer } from "../contribution-materializations";
import type { SqlExecutionAdapter } from "../execution/types";
import type { InternalOperationBackend } from "../operation-backend-core";
import type { CommonOperationStrategy } from "../operations/strategy";
import type { EngineAssembly } from "./assembly";
import type { CreateBaseSchemaMembersDeps } from "./members/base-schema-members";
import type { CreateContributionMembersDeps } from "./members/contribution-members";
import type { CreateGraphTemplateMembersDeps } from "./members/graph-template-members";
import type { CreateIdentityMembersDeps } from "./members/identity-members";
import type { CreateIndexMaterializationMembersDeps } from "./members/index-materialization-members";
import type { CreateKindRemovalMembersDeps } from "./members/kind-removal-members";

/** Resolved physical table names, uniform across dialects. */
export type EngineTableNames = ResolvedSqlTableNames;

/** Resolve the adapter's physical-provisioning policy once at construction. */
export function resolveSchemaProvisioning(
  value: unknown,
): SchemaProvisioning {
  if (value === undefined) return "dml-only";
  if (value === "dml-only" || value === "transactional") return value;
  throw new ConfigurationError(
    "schemaProvisioning must be 'dml-only' or 'transactional'.",
    { option: "schemaProvisioning", value },
  );
}

/**
 * The DDL primitives a profile owns. Every DDL statement any member group
 * emits goes through one of these rather than a bare `db.execute` /
 * `db.run`, so each dialect's wrinkle stays in one place: SQLite's DDL
 * bypasses its serialized queue on purpose, and PostgreSQL's `ensureTable`
 * carries a concurrent-create retry because two replicas can boot against
 * the same database at once.
 */
export type EngineProvisioning = Readonly<{
  /**
   * Runs ONE DDL statement with no concurrency handling — the semantics of
   * the adapter's own `executeDdl` member. Never use it for a create that
   * two booting processes can race; that is what `ensureTable` is for.
   */
  executeDdl: (ddl: string) => Promise<void>;
  /**
   * Runs one already-rendered `CREATE ...` statement idempotently, carrying
   * the dialect's concurrent-create retry where the engine needs one
   * (PostgreSQL). Every bootstrap and `ensure*` path that creates a relation
   * must use this arm, not `executeDdl`.
   */
  ensureTable: (ddl: string) => Promise<void>;
  /** The full set of base-schema DDL statements for a fresh bootstrap. */
  generateDdl: () => readonly string[];
  /**
   * PostgreSQL's additive-column migration for an index-materializations
   * table created before the build-claim columns existed. Absent on
   * dialects with no such migration to run.
   */
  ensureIndexMaterializationColumns?: (tableName: string) => Promise<void>;
  /**
   * The physical-schema introspection surface `createSqlBackend` forwards
   * onto the assembled backend's `catalog` member unchanged. Optional: a
   * profile that omits it produces a backend with no `catalog`, exactly
   * like any other optional `GraphBackend` member.
   */
  catalog?: BackendCatalogProbes;
  /**
   * The engine-revision and change-delta surface `createSqlBackend`
   * forwards onto the assembled backend's `lineage` member unchanged.
   * Optional: a profile that omits it produces a backend with no
   * `lineage`, exactly like any other optional `GraphBackend` member —
   * neither bundled Drizzle profile supplies one today, so a store's own
   * recorded-relations lineage (when history is on) is what backs the
   * capability instead.
   */
  lineage?: LineageMembers;
  /**
   * The engine-native recorded-time source and revision clock
   * `createSqlBackend` forwards onto the assembled backend's
   * `recordedTime` member unchanged. Optional: a profile that omits it
   * produces a backend with no `recordedTime`, exactly like any other
   * optional `GraphBackend` member — neither bundled Drizzle profile
   * supplies one today, so `history`/`revisionTracking` always allocate
   * TypeGraph's own recorded clock and relations instead. A profile that
   * declares this must also declare `lineage`; `createSqlBackend` refuses
   * one that declares only `recordedTime`.
   */
  recordedTime?: EngineRecordedTimeMembers;
}>;

/**
 * What a profile's `buildOperations` closure (wrapped, along with
 * `lateMembers`, into the opaque `assembly` {@link SqlEngineProfile} carries
 * — see `./assembly`) receives instead of only the contribution
 * materializer: the capabilities, fence plan, and fence target
 * {@link createSqlBackend} has already resolved from the profile's own
 * `declaredCapabilities`, before any member group — including the operation-
 * backend layer this builds — is assembled.
 *
 * `createSqlBackend` is the ONE place that runs `finalizeEngineCapabilities`
 * (`./capabilities`) and builds the fence target; a dialect's `buildOperations`
 * closure reads `capabilities` / `fenceTarget` off this context rather than
 * deriving its own copy, so the `InternalOperationBackend` it builds always
 * agrees with every late member and every mark site about what this backend
 * declares and which fence it resolves.
 */
export type EngineOperationsContext = Readonly<{
  /** The backend's capabilities after `finalizeEngineCapabilities` runs on `declaredCapabilities`. */
  capabilities: BackendCapabilities;
  /** The write-fence decision, resolved once from `fenceTarget`. */
  fencePlan: WriteFencePlan;
  /**
   * ONE fence target for the whole backend and every transaction-scoped one
   * it builds, built by `createSqlBackend` from the capabilities above and
   * marked first-party the same way the two bundled builders always did.
   */
  fenceTarget: WriteFenceTarget;
  /** The contribution materializer `createSqlBackend` built from `profile.contributionRuntime`. */
  contributionMaterializer: ContributionMaterializer;
  /**
   * Whether `profile` is the exact object `isFirstPartyProfile`
   * (`../../capabilities/write-fence`) recognizes — resolved once by
   * `createSqlBackend` and reused for every transaction-scoped backend a
   * dialect's `lateMembers` opens, so a handle it hands out carries the
   * SAME standing as the root rather than re-checking a copy, spread, or
   * otherwise derived profile that is a different object and is never
   * first-party. A dialect gates its own `markFirstPartyFactory` call on a
   * TypeGraph-opened transaction handle on this flag exactly as
   * `createSqlBackend` gates the root and fence-target marks on it;
   * `adoptTransaction` — a caller-driven, unaudited transaction lifetime —
   * passes `false` regardless of this flag, never this value.
   */
  isFirstParty: boolean;
}>;

/**
 * What a profile's late-bound members receive instead of the whole profile
 * or a partially built backend: everything {@link EngineOperationsContext}
 * carries, plus the assembled operation-backend layer and `self()` — a
 * thunk resolving to the exact backend object {@link createSqlBackend} is
 * building, safe to call only once that object has been returned to a
 * caller who could invoke it (i.e., never during the profile's own
 * construction).
 */
export type EngineAssemblyContext<TTx> = EngineOperationsContext &
  Readonly<{
    /** The operation-backend layer `createSqlBackend` built from the `buildOperations` closure the profile's assembly wraps (`./assembly`). */
    operations: InternalOperationBackend;
    /** Resolves to the backend `createSqlBackend` is building. */
    self: () => AdapterBackend<TTx>;
  }>;

/**
 * The late-bound member groups a profile supplies, assembled by
 * {@link createSqlBackend} from `EngineAssemblyContext`. `transactions`,
 * `rawSql`, `maintenance`, `trustedImport`, and `extensions` stay
 * dialect-owned forever — none of them is a mirror waiting to be extracted
 * into a shared `members/*.ts` file, because each genuinely differs in body
 * between engines (self-referential transaction framing, the raw-SQL escape
 * hatch's driver-level binding and decoding, a lock clause resolved from the
 * fence plan).
 */
export type EngineLateMembers<TTx> = Readonly<{
  /**
   * The backend's transaction-opening surface. Self-referential — a
   * transaction's own body calls back into the backend that opened it —
   * which is why these are late members reached through
   * `EngineAssemblyContext.self()` rather than head data. Also where
   * `contributionMaterializer`-dependent construction (a transaction-scoped
   * operation backend) stays dialect-owned, reached through
   * `ctx.contributionMaterializer` rather than a closed-over local.
   */
  transactions: Pick<
    AdapterBackend<TTx>,
    | "transaction"
    | "transactionWithNative"
    | "adoptTransaction"
    | "adoptSchemaWriteTransaction"
    | "schemaWriteTransaction"
  >;
  fence: Readonly<{
    /**
     * The internal, write-fence-holding transaction runner every
     * schema-commit method — and `createSqlBackend`'s own contribution-
     * rebuild wiring — delegates to. `fn` receives the full
     * `InternalOperationBackend` — including `commitSchemaVersion` and
     * `setActiveVersion`, which the narrower, publicly-reachable
     * `SchemaWriteTransactionBackend` deliberately omits — because this
     * runner is the trusted internal caller the lock exists to gate, not
     * the public `schemaWriteTransaction` surface a caller's preflight
     * runs against. Not itself an `AdapterBackend` member — SQLite's schema
     * lock is per-connection, so its implementation ignores `graphId`,
     * exactly like the uniform-signature wrapper it already exposes to its
     * contribution materializer.
     */
    runSchemaWriteTransaction: <T>(
      graphId: string,
      fn: (target: InternalOperationBackend) => Promise<T>,
    ) => Promise<T>;
  }>;
  /**
   * The backend's raw-SQL escape hatch. Dialect-owned because parameter
   * binding and result decoding both go through the driver directly here,
   * bypassing the operation-backend layer's own row mapping.
   */
  rawSql: Pick<TransactionBackend, "execute" | "executeRaw">;
  /**
   * Statistics/maintenance operations the query planner benefits from
   * (e.g. an `ANALYZE`-shaped refresh); dialect-specific SQL hidden behind
   * one uniform member.
   */
  maintenance: Pick<AdapterBackend<TTx>, "refreshStatistics">;
  trustedImport?: AdapterBackend<TTx>["trustedImport"];
  /**
   * Optional install/claim members — database extensions, index-
   * materialization claims, edge-match identity storage — that only some
   * dialects or configurations expose.
   */
  extensions?: Partial<
    Pick<
      AdapterBackend<TTx>,
      | "ensureExtension"
      | "ensureTrigramExtension"
      | "claimIndexMaterialization"
      | "releaseIndexMaterializationClaim"
      | "ensureEdgeMatchIdentityStorage"
    >
  >;
}>;

/**
 * What a profile supplies `createSqlBackend` to build the contribution
 * member group, beyond what the profile's own head already carries
 * (`dialect`, `fulltext`, `vector`) and what `createSqlBackend` derives
 * itself (`fenceTarget` from the finalized capabilities;
 * `ensureTable`/`execute`/`operationStrategy` from
 * `provisioning`/`execution`/`strategy`; and `schemaWriteTransaction` from
 * the fence's own late member, once it exists). See
 * `members/contribution-members.ts` for what each field does.
 */
export type ContributionRuntime = Omit<
  CreateContributionMembersDeps,
  | "dialect"
  | "fulltextStrategy"
  | "vectorStrategy"
  | "fenceTarget"
  | "ensureTable"
  | "execute"
  | "operationStrategy"
  | "schemaWriteTransaction"
>;

/**
 * What a profile supplies `createSqlBackend` to build the identity/
 * recorded-relation member group, beyond `ensureTable` (from `provisioning`)
 * and `contributionTableExists` (from the contribution member group
 * `createSqlBackend` builds first). See `members/identity-members.ts`.
 */
export type IdentityRuntime = Omit<
  CreateIdentityMembersDeps,
  "ensureTable" | "contributionTableExists"
>;

/**
 * What a profile supplies `createSqlBackend` to build the graph-template
 * member group, beyond `ensureTable` (from `provisioning`), `execute` (the
 * operation layer's own `execute`, once `createSqlBackend` has built it),
 * and `fencePlan`/`fenceTarget` (resolved and built once by `createSqlBackend`
 * before it builds this member group — the same pair every other member
 * group shares). See `members/graph-template-members.ts`.
 */
export type GraphTemplateRuntime = Omit<
  CreateGraphTemplateMembersDeps,
  "ensureTable" | "execute" | "fencePlan" | "fenceTarget"
>;

/**
 * What a profile supplies `createSqlBackend` to build the base-schema
 * lifecycle member group, beyond `ensureTable`/`executeDdl`/`generateDdl`
 * (from `provisioning`) and `ensureGraphTemplatesTable` (from the
 * graph-template member group `createSqlBackend` builds first). See
 * `members/base-schema-members.ts`.
 */
export type BaseSchemaRuntime = Omit<
  CreateBaseSchemaMembersDeps,
  "ensureTable" | "executeDdl" | "generateDdl" | "ensureGraphTemplatesTable"
>;

/**
 * What a profile supplies `createSqlBackend` to build the index-
 * materializations member group, beyond `ensureTable` /
 * `ensureIndexMaterializationColumns` (both from `provisioning`). See
 * `members/index-materialization-members.ts`.
 */
export type IndexMaterializationRuntime = Omit<
  CreateIndexMaterializationMembersDeps,
  "ensureTable" | "ensureIndexMaterializationColumns"
>;

/**
 * What a profile supplies `createSqlBackend` to build the kind-removals
 * member group, beyond `ensureTable` (from `provisioning`). See
 * `members/kind-removal-members.ts`.
 */
export type KindRemovalRuntime = Omit<
  CreateKindRemovalMembersDeps,
  "ensureTable"
>;

/**
 * Everything one SQL engine contributes to `createSqlBackend`: a HEAD of
 * data and dialect closures that exist before any backend object does,
 * including the opaque `assembly` that wraps the operation-backend builder
 * and the late-member factory for the members that need the assembled
 * pipeline (see the module doc comment for why the split exists).
 *
 * First-party standing — `resolveWriteFencePlan`'s dialect-derivation
 * fallback (sound only for the two bundled dialects) and the lazy
 * schema-fence lease `store/operations/write-transaction.ts` takes out
 * under `isFirstPartyFactory` — is not a field on this type. It is bound to
 * the exact object `buildSqliteEngineProfile` / `buildPostgresEngineProfile`
 * returned, via `isFirstPartyProfile` (`../../capabilities/write-fence`), an
 * identity check `createSqlBackend` runs on the `profile` argument it
 * receives. A copy, spread, or otherwise derived profile is a new object
 * that check has never seen, so it never carries this standing forward —
 * no field could grant it the way a spread grants every other key.
 */
export type SqlEngineProfile<TTx> = Readonly<{
  /**
   * The dialect this profile is for. `createSqlBackend` threads it through
   * unexamined — into the write-fence declaration line, the fence-target
   * marker, and the profile-refusal error — never branches on it itself.
   */
  dialect: SqlDialect;
  tableNames: EngineTableNames;
  /**
   * Every statement any shared member group issues goes through this
   * adapter's `execAll`/`execGet`/`execRun`, which is where a dialect
   * serializes its own SQL over its own driver.
   */
  execution: SqlExecutionAdapter;
  /**
   * The dialect's SQL-fragment strategy (table/column resolution, JSON and
   * locking-clause construction) the operation-backend layer assembles
   * its writes and reads against.
   */
  strategy: CommonOperationStrategy;
  /**
   * The dialect's full-text search strategy, or `undefined` when this
   * backend has no fulltext support (`fulltext: false`). Feeds capability
   * derivation and search compilation.
   */
  fulltext: FulltextStrategy | undefined;
  /** The dialect's vector-search strategy, or `undefined` when this connection has no vector extension loaded. */
  vector: VectorStrategy | undefined;
  /** The dialect's declared capabilities, before its capability tail runs. */
  declaredCapabilities: BackendCapabilities;
  /** Adapter policy for provisioning inside caller-owned schema transactions. */
  schemaProvisioning: SchemaProvisioning;
  /**
   * The serialized-resource verdict {@link createSqlBackend} records once,
   * before the backend object escapes (see `../../transaction-resource.ts`).
   */
  resourceAudit: BackendResourceAudit;
  /**
   * Declares whether a single statement outside an explicit transaction has
   * this engine's full durability and atomicity. `createSqlBackend` marks
   * the backend's bundled root autocommit-eligible on this word alone — a
   * profile that gets it wrong makes callers trust a write that is not yet
   * durable.
   */
  autocommit: Readonly<{ singleStatementDurable: boolean }>;
  provisioning: EngineProvisioning;
  /**
   * The lock-statement spelling this engine's fence plan carries when it
   * resolves to `lock`: `createSqlBackend` folds it into the one fence
   * target it builds, and the backend advertises it as `fenceSql`. Absent
   * on an engine whose fence is `engine-serialized`.
   */
  fenceSql?: FenceSql;
  /** Deps for the contribution-marker member group; see {@link ContributionRuntime}. */
  contributionRuntime: ContributionRuntime;
  /** Deps for the identity/recorded-relation member group; see {@link IdentityRuntime}. */
  identityRuntime: IdentityRuntime;
  /** Deps for the graph-template member group; see {@link GraphTemplateRuntime}. */
  graphTemplateRuntime: GraphTemplateRuntime;
  /** Deps for the base-schema lifecycle member group; see {@link BaseSchemaRuntime}. */
  baseSchemaRuntime: BaseSchemaRuntime;
  /** Deps for the index-materializations member group; see {@link IndexMaterializationRuntime}. */
  indexMaterializationRuntime: IndexMaterializationRuntime;
  /** Deps for the kind-removals member group; see {@link KindRemovalRuntime}. */
  kindRemovalRuntime: KindRemovalRuntime;
  /** The `GraphBackend` member of the same name. */
  close: () => Promise<void>;
  /**
   * This dialect's operation-backend builder and late-member factory,
   * wrapped opaquely — see {@link EngineAssembly} (`./assembly`) for what it
   * hides and why. Bundled-only: the two builders are the only callers of
   * `assembleEngine`, `createSqlBackend` resolves it once via
   * `resolveEngineAssembly`, and `deriveEngineProfile` carries a base
   * profile's `assembly` forward by reference rather than building its own.
   */
  assembly: EngineAssembly<TTx>;
}>;
